import { initializeApp } from "firebase/app";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

// LAZY AUTH: Only initialize when needed to prevent crash if API key is missing on production
let _auth: any = null;
export const getFirebaseAuth = async () => {
  if (!_auth) {
    const { getAuth } = await import("firebase/auth");
    _auth = getAuth(app);
  }
  return _auth;
};

/**
 * Dynamically import messaging and check support.
 * This is the safest way to prevent crashes on unsupported browsers (like some PCs).
 */
const getSafeMessaging = async () => {
  if (typeof window === 'undefined') return null;
  
  try {
    // Dynamically import only when needed
    const { isSupported, getMessaging } = await import("firebase/messaging");
    
    const supported = await isSupported();
    if (!supported) {
      console.debug('FCM is not supported in this browser environment.');
      return null;
    }
    
    return getMessaging(app);
  } catch (err) {
    console.warn('Firebase Messaging dynamic import or init failed:', err);
    return null;
  }
};

export const requestForToken = async (vapidKey: string) => {
  try {
    const messaging = await getSafeMessaging();
    if (!messaging) return null;

    const { getToken } = await import("firebase/messaging");
    
    // SAFEGUARD: iOS Safari (unless PWA) often completely lacks the Notification object.
    if (!('Notification' in window)) {
      console.warn('Notifications not supported in this browser environment.');
      return null;
    }
    
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      const currentToken = await getToken(messaging, {
        vapidKey: vapidKey,
      });
      return currentToken || null;
    }
    return null;
  } catch (err) {
    console.warn('An error occurred while retrieving token: ', err);
    return null;
  }
};

export const onMessageListener = async (callback: (payload: any) => void) => {
  try {
    const messaging = await getSafeMessaging();
    if (!messaging) return null;

    const { onMessage } = await import("firebase/messaging");
    return onMessage(messaging, (payload) => {
      callback(payload);
    });
  } catch (err) {
    console.warn('OnMessage listener setup failed:', err);
    return null;
  }
};
